import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useApi from '../../hooks/useApi';
import useIsClientAdmin from 'vl-common/src/hooks/useIsClientAdmin';
import useIsReferrerAdmin from 'vl-common/src/hooks/useIsReferrerAdmin';
import { Select } from 'antd';

export function PatientTags({
  patient_guid,
  insured,
  displayAlertMessage,
  updateTags = (tags: any[]) => {},
  selectedTags = [],
  isFilter = false,
  isExternal = false
}) {
  const { t } = useTranslation('common');
  const [tags, _setTags] = useState([]); // the current selected set of tags
  const [availableTags, setAvailableTags] = useState<string[]>([]); // either info_tags or filter_tags depending on isFilter
  const isClientAdmin = useIsClientAdmin();
  const isReferrerAdmin = useIsReferrerAdmin();
  const isAdmin = isClientAdmin || isReferrerAdmin;
  const [attempt, setAttempt] = useState(0);
  const { Api } = useApi();

  const setTagsAndNotifyUpdate = (tags) => {
    _setTags(tags);
    updateTags(tags);
  };

  useEffect(() => {
    if (insured === false) {
      return;
    }

    // NB: this causes the Ant Design Select list to unmount, which in turns causes it
    // to remount, and re-initialise, once the tags are loaded
    _setTags(null);
    // load this patient's tags

    if (patient_guid) {
      Api.referrers
        .getPatient(patient_guid)
        .then(
          ({
            data: {
              users: [{ info_tags, filter_tags }]
            }
          }) => {
            setTagsAndNotifyUpdate((isFilter ? filter_tags : info_tags) || []);
          }
        )
        .catch(console.error);
    }

    // load all the tags available for this patient
    if (!isExternal || (isExternal && patient_guid)) {
      Api.meta
        .getMetaMisc(patient_guid || null)
        .then((data) => {
          const {
            data: {
              meta: [{ info_tags, filter_tags }]
            }
          } = data;
          setAvailableTags((isFilter ? filter_tags : info_tags) || []);
        })
        .catch(console.error);
    }
  }, [patient_guid, insured]);

  useEffect(() => {
    if (selectedTags?.length && !tags) {
      _setTags(selectedTags);
    }
  }, [selectedTags, tags]);

  const maybeSetTags = (latestTags: string[]) => {
    // Referrers can apply existing info or filter tags to patients, they cannot create new info tags
    // Ref admins can apply existing filter tags to patients, they should be able to create a new info tag or apply existing info tags
    // Nothing can create a new filter tag
    const hasNewlyCreatedTags = latestTags.some((tag) => !availableTags.includes(tag));
    if (hasNewlyCreatedTags && !isAdmin) {
      displayAlertMessage('Please select from existing tags');
      setTagsAndNotifyUpdate(latestTags.filter((tag) => availableTags.includes(tag)));
      setAttempt((a) => a + 1);
    } else {
      setTagsAndNotifyUpdate(latestTags);
    }
  };

  if (insured === false) {
    return null;
  }

  return (
    <Select
      key={`${tags && patient_guid}${attempt}`}
      onChange={maybeSetTags}
      mode="tags"
      defaultValue={tags || []}
      style={{ width: '100%' }}
      placeholder={t('Please review available tags')}
      data-testid="patient-tag-select"
      options={availableTags.map((tag) => ({ value: tag, label: tag }))}
    />
  );
}

export default PatientTags;
